import { clipText, renderYaxisNumerical, renderGrid } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { interpolateColors } from '../../helpers/colors';
import { getSelectionHighlightColor, getStandardDataColor } from '../../../themes/themes';
import { renderYLabel } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getLabelSpaceSize } from '../../helpers/configParams';
import {
  getIntersectionslEnFilterStep,
  testIntersectionEquals,
} from '../../../../data/selection/selectionEnFilterIntersections';
import { currentTheme, getThemedGrayFraction } from '../../../themes/themes';
import { getUserError } from '../../../../components/miscUtils';

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
) {
  const showYAxis = !!config.displayYAxis;
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataSets = boundData.bindings.sets.data;
  const dataItemId = boundData.bindings.itemId.data;
  const name = boundData.bindings.sets.columnInfo.name;
  const setsColumnInfo = boundData.bindings.sets.columnInfo;

  const boundSummedValue = boundData.bindings.summedValue;
  const dataSummedValue = boundSummedValue?.data;
  const summedValueColumnInfo = boundSummedValue?.columnInfo;
  const hasSummedValue = !!dataSummedValue;

  const defaultTitle = hasSummedValue ? `${name} (total ${summedValueColumnInfo.name})` : name;
  const title = generateChartTitle(dataContext, dataBinding, config, defaultTitle);

  const itemCount = dataSets.length;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  let usedSetList = null;
  let usedSetMap: { [value: string]: boolean } | null = null;
  if (config.usedValues) {
    usedSetList = config.usedValues.split(';');
    usedSetMap = {};
    for (const usedSet of usedSetList) usedSetMap[usedSet] = true;
  }

  const filterInfo = getIntersectionslEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    setsColumnInfo.id,
  );

  interface TpSetDef {
    setId: string;
    setName: string;
    count: number;
    selectedCount: number;
    summedValue?: number;
    summedValueSelected?: number;
  }

  interface TpIntersectionDef {
    intersectionId: string;
    label: string;
    membershipIds: string[]; // categorical values IDs that represens the set this intersection contains
    membershipIndexes: number[]; // set indexes to which this intersection belongs
    count: number;
    selectedCount: number;
    summedValue?: number;
    summedValueSelected?: number;
    averageSetIndex: number;
  }

  let hasNegativeValues = false;
  function getRowValue(rowIdx: number): number {
    if (!hasSummedValue) return 1;
    const val = dataSummedValue[rowIdx];
    if (val === null || val === undefined || isNaN(val)) return 0;
    if (val < 0) hasNegativeValues = true;
    return val;
  }

  // Determine the sets
  const sets: TpSetDef[] = [];
  let setsMap: { [setId: string]: TpSetDef } = {};
  for (let rowIdx = 0; rowIdx < itemCount; rowIdx++) {
    const itemSets = dataSets[rowIdx];
    const rowVal = getRowValue(rowIdx);
    for (const setId of itemSets) {
      const setName = convertColumnDataToDisplayString(setsColumnInfo, setId);
      if (!usedSetMap || usedSetMap[setName]) {
        if (!setsMap[setId]) {
          const newSet: TpSetDef = {
            setId,
            setName,
            count: 0,
            selectedCount: 0,
            ...(hasSummedValue ? { summedValue: 0, summedValueSelected: 0 } : {}),
          };
          sets.push(newSet);
          setsMap[setId] = newSet;
        }
        setsMap[setId].count++;
        if (hasSummedValue) {
          setsMap[setId].summedValue! += rowVal;
        }
        if (isSelectedItem(selectedItemsMap, dataItemId[rowIdx])) {
          setsMap[setId].selectedCount++;
          if (hasSummedValue) {
            setsMap[setId].summedValueSelected! += rowVal;
          }
        }
      }
    }
  }

  if (hasSummedValue && hasNegativeValues) {
    console.warn('UpsetPlot: Negative values detected in summed numerical category. Totals capped at 0.');
  }

  if (hasSummedValue) {
    for (const set of sets) {
      set.summedValue = Math.max(0, set.summedValue!);
      set.summedValueSelected = Math.max(0, Math.min(set.summedValue, set.summedValueSelected!));
    }
  }

  // Sort the sets
  sets.sort((a, b) => {
    const valA = hasSummedValue ? a.summedValue! : a.count;
    const valB = hasSummedValue ? b.summedValue! : b.count;
    return valA - valB;
  });
  // Rebuild the set lookup index after sorting
  setsMap = {};
  const setId2Index: { [setId: string]: number } = {};
  for (let setIdx = 0; setIdx < sets.length; setIdx++) {
    setsMap[sets[setIdx].setId] = sets[setIdx];
    setId2Index[sets[setIdx].setId] = setIdx;
  }
  const setCount = sets.length;

  if (setCount > 50) {
    throw getUserError('Too many sets');
  }

  let maxInSet = 1;
  for (const set of sets) {
    const val = hasSummedValue ? set.summedValue! : set.count;
    maxInSet = Math.max(maxInSet, val);
  }

  // Determine the intersections
  const intersections: TpIntersectionDef[] = [];
  let intersectionsMap: { [id: string]: TpIntersectionDef } = {};
  for (let rowIdx = 0; rowIdx < itemCount; rowIdx++) {
    const itemSets = dataSets[rowIdx];
    const rowVal = getRowValue(rowIdx);
    const setMembershipsMask = new Array(sets.length).fill(false);
    for (const setId of itemSets) setMembershipsMask[setId2Index[setId]] = true;
    const intersectionId =
      'intersectionMask_' + setMembershipsMask.map((membership) => (membership ? '1' : '0')).join();
    if (!intersectionsMap[intersectionId]) {
      const membershipIds: string[] = [];
      const membershipIndexes: number[] = [];
      for (let setIdx = 0; setIdx < sets.length; setIdx++)
        if (setMembershipsMask[setIdx]) {
          membershipIds.push(sets[setIdx].setId);
          membershipIndexes.push(setIdx);
        }
      const newIntersection: TpIntersectionDef = {
        label: membershipIndexes.map((idx) => sets[idx].setName).join(' + '),
        intersectionId,
        count: 0,
        membershipIndexes,
        membershipIds,
        selectedCount: 0,
        ...(hasSummedValue ? { summedValue: 0, summedValueSelected: 0 } : {}),
        averageSetIndex: membershipIndexes.reduce((a, b) => a + b, 0) / membershipIndexes.length,
      };
      intersections.push(newIntersection);
      intersectionsMap[intersectionId] = newIntersection;
    }
    intersectionsMap[intersectionId].count++;
    if (hasSummedValue) {
      intersectionsMap[intersectionId].summedValue! += rowVal;
    }
    if (isSelectedItem(selectedItemsMap, dataItemId[rowIdx])) {
      intersectionsMap[intersectionId].selectedCount++;
      if (hasSummedValue) {
        intersectionsMap[intersectionId].summedValueSelected! += rowVal;
      }
    }
  }

  if (hasSummedValue) {
    for (const intersection of intersections) {
      intersection.summedValue = Math.max(0, intersection.summedValue!);
      intersection.summedValueSelected = Math.max(
        0,
        Math.min(intersection.summedValue, intersection.summedValueSelected!),
      );
    }
  }

  // Sort the intersections
  intersections.sort((a, b) => {
    const valA = hasSummedValue ? a.summedValue! : a.count;
    const valB = hasSummedValue ? b.summedValue! : b.count;
    if (Math.abs(valA - valB) < 1e-9) return b.averageSetIndex - a.averageSetIndex;
    return valB - valA;
  });

  // Rebuild the intersections lookup table after sorting
  intersectionsMap = {};
  for (const intersection of intersections) intersectionsMap[intersection.intersectionId] = intersection;

  const intersectionCount = intersections.length;

  let maxInIntersection = 1;
  for (const intersection of intersections) {
    const val = hasSummedValue ? intersection.summedValue! : intersection.count;
    maxInIntersection = Math.max(maxInIntersection, val);
  }

  function renderSetLabels(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv, pixelInvConv } = renderContext;
    const ctx = renderContext.canvasContext;
    const fontSize = Math.min(9, 0.75 * pixelInvConv(Math.abs(mapy(1 / setCount) - mapy(0))));
    ctx.fillStyle = currentTheme.colors.text.toCanvasString();
    renderContext.setFont(fontSize);
    ctx.textAlign = 'right';
    const offset = renderContext.areaXMax - pixelConv(2);

    const unSelectedFill = getThemedGrayFraction(0.9).toCanvasString();
    const selectedFill = interpolateColors(
      getStandardDataColor(),
      currentTheme.colors.background,
      0.25,
    ).toCanvasString();

    for (let setIdx = 0; setIdx < setCount; setIdx++) {
      const set = sets[setIdx];
      const setVal = hasSummedValue ? set.summedValue! : set.count;
      const setSelectedVal = hasSummedValue ? set.summedValueSelected! : set.selectedCount;
      const y1 = mapy((setIdx + 0.1) / setCount);
      const y2 = mapy((setIdx + 0.9) / setCount);
      ctx.fillStyle = unSelectedFill;
      ctx.fillRect(mapx(0), y1, mapx(setVal) - mapx(0), y2 - y1);
      ctx.fillStyle = selectedFill;
      ctx.fillRect(mapx(0), y1, mapx(setSelectedVal) - mapx(0), y2 - y1);
      if (fontSize > 3) {
        ctx.fillStyle = currentTheme.colors.text.toCanvasString();
        const labelClipped = clipText(
          ctx,
          set.setName,
          renderContext.areaXMax - renderContext.areaXMin - pixelConv(2),
          fontSize,
        );
        ctx.fillText(labelClipped, offset, mapy((setIdx + 0.5) / setCount) - pixelConv(fontSize / 2));
      }
    }
  }

  function renderBars(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    if (showYAxis) {
      renderGrid(renderContext, false, true);
    }

    ctx.fillStyle;
    const unSelectedFill = getThemedGrayFraction(0.8).toCanvasString();
    const selectedFill = interpolateColors(
      getStandardDataColor(),
      currentTheme.colors.background,
      0.75,
    ).toCanvasString();

    for (let intersectionIdx = 0; intersectionIdx < intersectionCount; intersectionIdx++) {
      const intersection = intersections[intersectionIdx];
      const interVal = hasSummedValue ? intersection.summedValue! : intersection.count;
      const interSelectedVal = hasSummedValue ? intersection.summedValueSelected! : intersection.selectedCount;
      const x1 = mapx((intersectionIdx + 0.1) / intersectionCount);
      const x2 = mapx((intersectionIdx + 0.9) / intersectionCount);
      ctx.fillStyle = unSelectedFill;
      ctx.fillRect(x1, mapy(0), x2 - x1, mapy(interVal) - mapy(0));
      ctx.fillStyle = selectedFill;
      ctx.fillRect(x1, mapy(0), x2 - x1, mapy(interSelectedVal) - mapy(0));
    }
  }

  function renderSets(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;
    const cellSizePixels = Math.min(
      Math.abs(mapx(1 / intersectionCount) - mapx(0)),
      Math.abs(mapy(1 / setCount) - mapy(0)),
    );

    ctx.fillStyle = getThemedGrayFraction(0.9).toCanvasString();
    for (let setIdx = 0; setIdx < setCount; setIdx += 2) {
      const y1 = mapy((setIdx + 0.1) / setCount);
      const y2 = mapy((setIdx + 0.9) / setCount);
      ctx.fillRect(renderContext.areaXMin, y1, renderContext.areaXMax - renderContext.areaXMin, y2 - y1);
    }

    const discRadius = 0.3 * cellSizePixels;
    const hBarWidth = 0.1 * cellSizePixels;
    for (let intersectionIdx = 0; intersectionIdx < intersectionCount; intersectionIdx++) {
      const intersection = intersections[intersectionIdx];

      let isInFilter = false;
      if (filterInfo) {
        for (const filterIntersection of filterInfo.intersections)
          if (testIntersectionEquals(filterIntersection, intersection.membershipIds)) isInFilter = true;
      }
      if (isInFilter) {
        ctx.fillStyle = getSelectionHighlightColor().opacity(0.25).toCanvasString();
        ctx.fillRect(
          mapx((intersectionIdx + 0.1) / intersectionCount),
          renderContext.areaYMin,
          mapx((intersectionIdx + 0.9) / intersectionCount) - mapx((intersectionIdx + 0.1) / intersectionCount),
          renderContext.areaYMax - renderContext.areaYMin,
        );
      }

      let minYCent = 1e99;
      let maxYCent = -1e99;
      const xCent = mapx((intersectionIdx + 0.5) / intersectionCount);
      for (let setIdx = 0; setIdx < setCount; setIdx++) {
        const yCent = mapy((setIdx + 0.5) / setCount);
        if (intersection.membershipIndexes.includes(setIdx)) {
          ctx.fillStyle = getStandardDataColor().toCanvasString();
          minYCent = Math.min(minYCent, yCent);
          maxYCent = Math.max(maxYCent, yCent);
        } else {
          ctx.fillStyle = currentTheme.colors.text.opacity(0.1).toCanvasString();
        }
        ctx.beginPath();
        ctx.arc(xCent, yCent, discRadius, 0, 2 * Math.PI);
        ctx.fill();
      }
      if (maxYCent > minYCent) {
        ctx.fillStyle = getStandardDataColor().toCanvasString();
        ctx.fillRect(xCent - hBarWidth, minYCent, 2 * hBarWidth, maxYCent - minYCent);
      }
    }
  }

  const hasLegend = false;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;
  const labselSpaceWidth = getLabelSpaceSize(config.setsLabelSpace);
  const barsHeight = 150;

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(-0.05 * maxInIntersection, maxInIntersection * 1.05);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding + labselSpaceWidth), RenderMaxDiffAbs(padding + 0));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMinDiffAbs(padding + chartDims.headerHeight + barsHeight),
  );

  const areaBars = {
    id: 'bars',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    // canAreaSelect: false,
    render: renderBars,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaBars,
    {
      id: 'setLabels',
      logicalRangeX: createLogicalRange(maxInSet * 1.05, -0.01 * maxInSet),
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + labselSpaceWidth)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + chartDims.headerHeight + barsHeight),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanY: true,
      render: renderSetLabels,
    },
    {
      id: 'sets',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + chartDims.headerHeight + barsHeight),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      zoomPanY: true,
      render: renderSets,
    },
    {
      id: 'header',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  if (showYAxis) {
    chartAreas.push({
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(
        RenderMinDiffAbs(padding + labselSpaceWidth - chartDims.yAxisWidth),
        RenderMinDiffAbs(padding + labselSpaceWidth),
      ),
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    });
  }

  // if (hasLegend) {
  //   chartAreas.push({
  //     id: 'legend',
  //     renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
  //     renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
  //     render: renderLegend,
  //   });
  // }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaBars,
    dataTable,
    dataValue: dataSets,
    setsColumnInfo,
    name,
    hasSummedValue,
    summedValueColumnInfo,
    itemCount,
    dataItemId,
    sets,
    intersections,
    intersectionsMap,
    title,
  };
}
